import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FilePlus, AlertCircle, Loader2 } from "lucide-react";
import { PageTemplate } from "@/types";

interface CreatePageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    name: string;
    description: string;
    template: PageTemplate;
  }) => Promise<void>;
  defaultName?: string;
}

export const CreatePageModal: React.FC<CreatePageModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultName = "",
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [template, setTemplate] = useState<PageTemplate>(PageTemplate.BLANK);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Auto-populate defaultName on open, reset on close
  useEffect(() => {
    if (open) {
      setName(defaultName);
      setDescription("");
      setTemplate(PageTemplate.BLANK);
      setIsSubmitting(false);
      setNameError(null);
      setSubmitError(null);
    }
  }, [open, defaultName]);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError("Page name is required");
      return;
    }
    if (trimmed.length > 60) {
      setNameError("Page name must be 60 characters or fewer");
      return;
    }

    setNameError(null);
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await onSubmit({ name: trimmed, description, template });
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to create page"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isSubmitting) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const nameLength = name.length;
  const charCountColor =
    nameLength > 60
      ? "text-destructive"
      : nameLength >= 50
        ? "text-amber-500"
        : "text-muted-foreground";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="light" className="sm:max-w-md p-0">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <FilePlus className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-card-foreground">
                Create new page
              </h3>
              <p className="text-sm text-muted-foreground">
                Add a new page to your project with a template and settings.
              </p>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="border-t border-border" />

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Page Name */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label
                htmlFor="create-page-name"
                className="text-sm font-medium text-foreground"
              >
                Page name <span className="text-destructive">*</span>
              </Label>
              <span className={`text-xs tabular-nums ${charCountColor}`}>
                {nameLength}/60
              </span>
            </div>
            <Input
              id="create-page-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
              onKeyDown={handleNameKeyDown}
              placeholder="e.g. Dashboard Overview"
              className={`h-10 bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary ${
                nameError
                  ? "border-destructive focus:border-destructive focus:ring-destructive"
                  : ""
              }`}
              autoFocus
              disabled={isSubmitting}
            />
            {nameError && (
              <div className="flex items-center gap-1.5 text-destructive">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                <p className="text-xs">{nameError}</p>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label
              htmlFor="create-page-description"
              className="text-sm font-medium text-foreground"
            >
              Description{" "}
              <span className="text-muted-foreground font-normal">(Optional)</span>
            </Label>
            <Textarea
              id="create-page-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What will this page contain?"
              rows={3}
              className="resize-none bg-background border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary"
              disabled={isSubmitting}
            />
          </div>

          {/* Template Selector */}
          <div className="space-y-2">
            <Label
              htmlFor="create-page-template"
              className="text-sm font-medium text-foreground"
            >
              Template
            </Label>
            <Select
              value={template}
              onValueChange={(value: PageTemplate) => setTemplate(value)}
              disabled={isSubmitting}
            >
              <SelectTrigger
                id="create-page-template"
                className="h-10 bg-background border-border text-foreground focus:border-primary focus:ring-primary"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PageTemplate.BLANK}>
                  Blank Page
                </SelectItem>
                <SelectItem value={PageTemplate.DASHBOARD}>
                  Dashboard
                </SelectItem>
                <SelectItem value={PageTemplate.REPORT}>
                  Report
                </SelectItem>
                <SelectItem value={PageTemplate.FORM}>
                  Form
                </SelectItem>
                <SelectItem value={PageTemplate.ANALYTICS}>
                  Analytics
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Submit Error Banner */}
          {submitError && (
            <div className="flex items-start gap-2 rounded-lg bg-destructive/10 border border-destructive/20 p-3">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <p className="text-sm text-destructive">{submitError}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 px-6 py-4 border-t border-border bg-muted/50">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="sm:w-auto w-full border-border text-card-foreground hover:bg-muted"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !name.trim()}
            className="sm:w-auto w-full min-w-[120px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Creating...
              </>
            ) : (
              "Create page"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
